/**
 * SQL Error Analyzer
 * Analyzes the user's SQL query to identify likely mistakes and suggest fixes
 */

import { findMissingFromJoin, extractFromTableRefs } from './fromClauseJoinCheck';
import { findMissingSelectComma } from './selectListCheck';
import { findKeywordTypo as findAndOrKeywordTypo } from './keywordTypoCheck';
import { findSqlStructureIssue } from './sqlStructureCheck';

/** Common Oracle Fusion tables (for "did you mean" when API tables unavailable) */
const COMMON_TABLES = [
  'per_all_people_f', 'per_person_names_f', 'per_people_f', 'per_people_stl',
  'hr_all_positions_f', 'per_all_assignments_f', 'per_jobs_f', 'per_grades_f',
  'per_organizations_f', 'per_departments_f', 'per_locations_f',
  'pqp_gb_accrual_plans_f', 'pqp_gb_accrual_plan_units_f',
  'ff_user_entities_f', 'ff_database_items', 'ff_formulas_f'
];

/** Common SQL keyword typos */
const KEYWORD_TYPOS = [
  { wrong: /==/, right: '=' },
  { wrong: /\bFORM\b/i, right: 'FROM' },
  { wrong: /\bFRO\b/i, right: 'FROM' },
  { wrong: /\bFRROM\b/i, right: 'FROM' },
  { wrong: /\bFROM\s+FROM\b/i, right: 'FROM' },
  { wrong: /\bSELEC\b/i, right: 'SELECT' },
  { wrong: /\bSELET\b/i, right: 'SELECT' },
  { wrong: /\bSELCT\b/i, right: 'SELECT' },
  { wrong: /\bSLECT\b/i, right: 'SELECT' },
  { wrong: /\bSELCET\b/i, right: 'SELECT' },
  { wrong: /\bdistint\b/i, right: 'DISTINCT' },
  { wrong: /\bWHREE\b/i, right: 'WHERE' },
  { wrong: /\bTABLE\b/i, right: 'TABLE' },
  { wrong: /\bINTO\s+INTO\b/i, right: 'INTO' },
  { wrong: /\bORDRE\b/i, right: 'ORDER' },
  { wrong: /\bGROUB\b/i, right: 'GROUP' },
  { wrong: /\bJOIN\s+JOIN\b/i, right: 'JOIN' },
];

/**
 * Extract all table names from FROM and JOIN clauses (for ORA-00942 when we can't identify which one)
 */
function extractAllTableNames(query) {
  if (!query || typeof query !== 'string') return [];
  const tables = [];
  // Match: FROM/JOIN followed by optional schema, table name, optional alias
  const re = /\b(?:FROM|JOIN)\s+([a-zA-Z0-9_$.]+)/gi;
  let m;
  while ((m = re.exec(query)) !== null) {
    const name = m[1].trim();
    if (name && /^[a-zA-Z]/.test(name) && !tables.includes(name)) tables.push(name);
  }
  return tables;
}

/** Levenshtein distance - for fuzzy matching */
function leven(a, b) {
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

/**
 * Find similar table names - returns best matches for "did you mean?"
 * @param {string} typedTable - Table name from the query
 * @param {string[]} knownTables - Valid table names (from API or catalog)
 * @returns {string[]} Up to 5 suggested table names
 */
export function findSimilarTables(typedTable, knownTables = []) {
  if (!typedTable || typeof typedTable !== 'string') return [];
  const typed = typedTable.trim();
  const typedNorm = typed.replace(/_+$/, '').toLowerCase();
  const pool = knownTables.length > 0 ? knownTables : COMMON_TABLES;

  const tableNames = pool.map(t => typeof t === 'string' ? t : (t?.id ?? t?.name ?? ''));
  const candidates = [];

  for (const name of tableNames) {
    if (!name) continue;
    const n = name.toLowerCase();
    let score = 999;

    // Exact match
    if (n === typedNorm) return [name];

    // Typed is prefix of table (e.g. per_all_people -> per_all_people_f)
    if (n.startsWith(typedNorm) || n.startsWith(typedNorm.replace(/_$/, ''))) {
      score = n.length - typedNorm.length;
    }
    // Table is prefix of typed (extra chars in typed)
    else if (typedNorm.startsWith(n) || typedNorm.replace(/_+$/, '').startsWith(n)) {
      score = 5 + (typedNorm.length - n.length);
    }
    // Similarity
    else {
      const dist = leven(typedNorm, n);
      if (dist <= 3) score = 10 + dist;
    }

    if (score < 999) candidates.push({ name, score });
  }

  return candidates
    .sort((a, b) => a.score - b.score)
    .slice(0, 5)
    .map(c => c.name);
}

/**
 * Find similar column names from catalog / loaded columns.
 * @param {string} typedColumn
 * @param {Array<string|{column_name?:string,name?:string}>} knownColumns
 * @returns {string[]}
 */
export function findSimilarColumns(typedColumn, knownColumns = []) {
  if (!typedColumn || typeof typedColumn !== 'string') return [];
  const typed = typedColumn.trim().replace(/^["']|["']$/g, '');
  const bare = typed.includes('.') ? typed.split('.').pop() : typed;
  const typedNorm = bare.toLowerCase();
  const pool = (knownColumns || [])
    .map((c) => (typeof c === 'string' ? c : c?.column_name || c?.name || ''))
    .filter(Boolean);
  if (pool.length === 0) return [];

  const candidates = [];
  for (const name of pool) {
    const n = name.toLowerCase();
    let score = 999;
    if (n === typedNorm) return [name];
    if (n.startsWith(typedNorm) || typedNorm.startsWith(n)) {
      score = Math.abs(n.length - typedNorm.length);
    } else {
      const dist = leven(typedNorm, n);
      if (dist <= 3) score = 10 + dist;
    }
    if (score < 999) candidates.push({ name, score });
  }
  return candidates
    .sort((a, b) => a.score - b.score)
    .slice(0, 5)
    .map((c) => c.name);
}

/**
 * Locate an identifier in the user's SQL for IDE-style underline.
 * Prefers the last occurrence in SELECT/WHERE/ORDER/GROUP/HAVING/ON (typical bad column site).
 * @returns {{ line: number, column: number, index: number, content: string } | null}
 */
export function locateIdentifierInQuery(query, identifier) {
  if (!query || !identifier || typeof query !== 'string') return null;
  const raw = String(identifier).trim().replace(/^["']|["']$/g, '');
  if (!raw) return null;
  const bare = raw.includes('.') ? raw.split('.').pop() : raw;
  // Match bare word or alias.bare (word boundaries)
  const re = new RegExp(
    `(?:^|[^A-Za-z0-9_$#])((?:[A-Za-z0-9_$#]+\\.)?${bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?![A-Za-z0-9_$#])`,
    'gi'
  );
  let match;
  let lastPreferred = null;
  let lastAny = null;
  while ((match = re.exec(query)) !== null) {
    const matched = match[1];
    const index = match.index + (match[0].length - matched.length);
    const before = query.slice(0, index);
    const lines = before.split(/\r?\n/);
    const line = lines.length;
    const column = (lines[lines.length - 1] || '').length + 1;
    const content = query.split(/\r?\n/)[line - 1] || '';
    const loc = { line, column, index, content, matched };
    lastAny = loc;
    if (
      /\b(SELECT|WHERE|ORDER\s+BY|GROUP\s+BY|HAVING|ON|AND|OR|SET)\b[\s\S]*$/i.test(
        before.toUpperCase()
      )
    ) {
      lastPreferred = loc;
    }
  }
  return lastPreferred || lastAny;
}

/**
 * Check for common SQL keyword typos in the query
 * @param {string} query - The SQL query
 * @returns {{ typo: string, suggestion: string, at: string } | null}
 */
export function findKeywordTypo(query) {
  if (!query || typeof query !== 'string') return null;
  for (const { wrong, right } of KEYWORD_TYPOS) {
    const m = query.match(wrong);
    if (m) {
      return { typo: m[0], suggestion: right, at: m[0] };
    }
  }
  return null;
}

/**
 * Analyze query + error to produce a clear, actionable explanation
 * @param {string} errorCode - ORA-00942, etc.
 * @param {string} message - Error message text
 * @param {string} query - The SQL query
 * @param {Object} context - { tables, objectName, objectType, fixes }
 * @returns {Object} { plainExplanation, didYouMean, typoSuggestion, highlightedPart, fixes, tablesInQuery }
 */
export function analyzeSqlError(errorCode, message, query, context = {}) {
  const result = {
    plainExplanation: null,
    didYouMean: null,
    typoSuggestion: null,
    highlightedPart: null,
    fixes: Array.isArray(context.fixes) ? [...context.fixes] : [],
    tablesInQuery: null,
  };

  const tables = context.tables || [];
  const tableNames = tables
    .map((t) => (typeof t === 'string' ? t : t?.id ?? t?.name ?? ''))
    .filter(Boolean);
  const columns = context.columns || [];
  const knownObject = context.objectName || null;
  const code = (errorCode || '').toUpperCase();

  // ORA-00942: table or view does not exist
  if (code === 'ORA-00942') {
    const msgTableMatch =
      message.match(/table or view\s+["']?([a-zA-Z0-9_$.]+)["']?/i) ||
      message.match(/object\s+["']?([a-zA-Z0-9_$.]+)["']?\s+does not exist/i);
    const tableName = knownObject || (msgTableMatch && msgTableMatch[1]) || null;
    const tablesInQuery = extractAllTableNames(query);

    if (tableName) {
      result.highlightedPart = tableName;
      result.plainExplanation = `Table or view "${tableName}" was not found (or you lack SELECT privilege).`;
      const similar = findSimilarTables(tableName, tableNames).filter(
        (t) => t.toLowerCase() !== tableName.toLowerCase()
      );
      if (similar.length > 0) result.didYouMean = similar;
      const loc = locateIdentifierInQuery(query || '', tableName);
      if (loc) result.location = loc;
      if (result.fixes.length === 0) {
        result.fixes = [
          `Verify "${tableName}" spelling against your connection catalog`,
          'Confirm SELECT privilege on that object',
        ];
      }
    } else if (tablesInQuery.length === 1) {
      const only = tablesInQuery[0];
      result.highlightedPart = only;
      result.plainExplanation = `Table or view "${only}" was not found (or you lack SELECT privilege).`;
      const similar = findSimilarTables(only, tableNames).filter(
        (t) => t.toLowerCase() !== only.toLowerCase()
      );
      if (similar.length > 0) result.didYouMean = similar;
      const loc = locateIdentifierInQuery(query || '', only);
      if (loc) result.location = loc;
    } else if (tablesInQuery.length > 1) {
      result.plainExplanation =
        'A table or view in your query was not found. Oracle did not name which one — check each FROM/JOIN name, or use “Find which table”.';
      result.tablesInQuery = tablesInQuery;
      result.fixes = result.fixes.length
        ? result.fixes
        : ['Check each name in FROM / JOIN', 'Use Find which table below'];
    } else {
      result.plainExplanation = 'A table or view in your query was not found. Check your FROM clause.';
    }
  }

  // ORA-00904: invalid column — name it, suggest from catalog, locate in SQL
  else if (code === 'ORA-00904') {
    const quotedMatch = message.match(/["']([a-zA-Z0-9_$.]+)["']/);
    const col = knownObject || (quotedMatch && quotedMatch[1]) || null;
    if (col) {
      const bare = col.includes('.') ? col.split('.').pop() : col;
      result.highlightedPart = col;
      result.plainExplanation = `Column "${col}" is invalid for this query (wrong name, wrong alias, or not in scope).`;
      const similar = findSimilarColumns(bare, columns).filter(
        (c) => c.toLowerCase() !== bare.toLowerCase()
      );
      if (similar.length > 0) result.didYouMean = similar;
      const loc = locateIdentifierInQuery(query || '', col);
      if (loc) result.location = loc;
      const tablesInQuery = extractAllTableNames(query);
      result.fixes = result.fixes.length
        ? result.fixes
        : [
            similar.length
              ? `Did you mean ${similar.slice(0, 3).map((s) => `"${s}"`).join(', ')}?`
              : `Check spelling of "${bare}"`,
            tablesInQuery.length > 1
              ? 'Qualify the column with a table alias (e.g. ppf.person_id)'
              : 'Confirm the column exists on the table in your FROM clause',
          ];
    } else {
      result.plainExplanation = 'A column name is invalid or not in scope for this query.';
    }
  }

  // ORA-00918: column ambiguously defined — suggest qualifiers from FROM
  else if (code === 'ORA-00918') {
    const quotedMatch = message.match(/["']([a-zA-Z0-9_$.]+)["']/) || message.match(/\bcolumn\s+([a-zA-Z0-9_$#]+)/i);
    const col = knownObject || (quotedMatch && quotedMatch[1]) || null;
    const fromRefs = extractFromTableRefs(query || '');
    const qualifiers = fromRefs.map((r) => r.qualifier).filter(Boolean);
    if (col) {
      result.highlightedPart = col;
      const bare = col.includes('.') ? col.split('.').pop() : col;
      result.plainExplanation = `Column "${bare}" is ambiguous — more than one table in FROM could own it.`;
      const loc = locateIdentifierInQuery(query || '', bare);
      if (loc) result.location = loc;
      if (qualifiers.length >= 2) {
        result.typoSuggestion = `Qualify it, e.g. ${qualifiers
          .slice(0, 3)
          .map((q) => `${q}.${bare}`)
          .join(' or ')}.`;
        result.fixes = qualifiers.slice(0, 4).map((q) => `Use ${q}.${bare}`);
      } else {
        result.fixes = result.fixes.length
          ? result.fixes
          : [`Qualify "${bare}" with a table alias (e.g. t.${bare})`];
      }
    } else {
      result.plainExplanation =
        'A column name appears in more than one table. Qualify it with a table alias.';
      if (qualifiers.length >= 2) {
        result.typoSuggestion = `Available qualifiers: ${qualifiers.join(', ')}`;
        result.fixes = ['Pick the correct alias and write alias.column_name'];
      }
    }
  }

  // ORA-00923: FROM keyword not found - often means typo like FORM
  else if (code === 'ORA-00923') {
    const typo = findKeywordTypo(query);
    if (typo) {
      result.typoSuggestion = `Did you mean "${typo.suggestion}" instead of "${typo.typo}"?`;
      result.plainExplanation = `Likely typo: "${typo.typo}" → "${typo.suggestion}".`;
      result.fixes = [`Replace ${typo.typo} with ${typo.suggestion}`];
    } else {
      result.plainExplanation = 'FROM was expected here. Check SELECT … FROM structure.';
    }
  }

  // ORA-00936: missing expression
  else if (code === 'ORA-00936') {
    result.plainExplanation =
      'An expression is incomplete — often a trailing comma, empty SELECT item, or operator without a right-hand side.';
  }

  // ORA-00933: SQL not properly ended
  else if (code === 'ORA-00933') {
    result.plainExplanation =
      'SQL not properly ended — often an extra keyword, bad clause order, or semicolon inside a subquery.';
  }

  // ORA-00920: invalid relational operator
  else if (code === 'ORA-00920') {
    result.plainExplanation = 'Invalid comparison operator in WHERE, JOIN, or HAVING.';
    if ((query || '').includes('==')) {
      result.typoSuggestion = 'Oracle uses = for equality, not ==.';
      result.fixes = ['Change == to ='];
    } else if (/[=<>]\s*$|[=<>]\s*(?:AND|OR|\))/i.test(query || '')) {
      result.typoSuggestion = 'A comparison operator is missing its right-hand value.';
    }
  }

  // ORA-00907: often a lie — AND typo, reserved alias, or missing comma/JOIN in FROM
  else if (code === 'ORA-00907') {
    const andOrTypo = findAndOrKeywordTypo(query || '');
    const structure = findSqlStructureIssue(query || '');
    if (andOrTypo) {
      result.plainExplanation = `${andOrTypo.error} — ${andOrTypo.suggestion}`;
      result.typoSuggestion = andOrTypo.suggestion;
      result.highlightedPart = andOrTypo.wrong;
      result.location = { line: andOrTypo.line, column: andOrTypo.column };
      result.fixes = [andOrTypo.suggestion, `Replace "${andOrTypo.wrong}" with "${andOrTypo.right}"`];
    } else if (structure) {
      result.plainExplanation = structure.error;
      result.typoSuggestion = structure.suggestion;
      result.highlightedPart = structure.wrong || null;
      result.location = { line: structure.line, column: structure.column };
      result.fixes = [structure.suggestion];
    } else {
    const missingJoin = findMissingFromJoin(query || '');
    if (missingJoin) {
      result.plainExplanation = missingJoin.error;
      result.typoSuggestion = missingJoin.suggestion;
      result.highlightedPart = missingJoin.nextTable;
      result.fixes = [
        `Add a comma before ${missingJoin.nextTable}, or use JOIN … ON …`,
        'Oracle reported ORA-00907, but the real issue is the FROM list — not a parenthesis',
      ];
    } else {
      const queryUpper = (query || '').toUpperCase();
      const hasFrom = /\bFROM\b/.test(queryUpper);
      const hasAnd = /\bAND\b/.test(queryUpper);
      const hasWhere = /\bWHERE\b/.test(queryUpper);
      if (hasFrom && hasAnd && !hasWhere) {
        result.plainExplanation =
          'Filter conditions need WHERE (not AND alone after the table name).';
        result.typoSuggestion =
          "Example: SELECT * FROM table_name WHERE language = 'US'";
        result.fixes = ['Insert WHERE before your first filter condition'];
      } else {
        const typo = findKeywordTypo(query);
        const invalidColLiteral = (query || '').match(/(\w+\.\w+)\s+'[^']+'\s+(\w+)/);
        if (typo) {
          result.typoSuggestion = `Did you mean "${typo.suggestion}" instead of "${typo.typo}"?`;
          result.plainExplanation = `Typo "${typo.typo}" can confuse the parser into reporting a parenthesis error.`;
          result.fixes = [`Replace ${typo.typo} with ${typo.suggestion}`];
        } else if (invalidColLiteral) {
          result.plainExplanation = `Invalid alias syntax near "${invalidColLiteral[0]}". Use AS for aliases.`;
          result.highlightedPart = invalidColLiteral[0];
          result.fixes = [`Use ${invalidColLiteral[1]} AS ${invalidColLiteral[2]}`];
        } else {
          const parenInfo = findParenthesisProblem(query);
          result.plainExplanation = parenInfo
            ? `Unbalanced parentheses near line ${parenInfo.line}.`
            : 'A parenthesis is missing or mismatched.';
        }
      }
    }
    }
  } else if (code === 'SQL SYNTAX' || /syntax/i.test(code)) {
    result.plainExplanation = message || 'Local syntax check failed.';
  } else if (message) {
    // Unknown ORA — don’t invent tips; show the message clearly
    result.plainExplanation = knownObject
      ? `Problem involving "${knownObject}": ${message}`
      : message;
  }

  return result;
}

/**
 * Find approximate line where parenthesis balance goes wrong
 */
function findParenthesisProblem(query) {
  if (!query) return null;
  const lines = query.split(/\r?\n/);
  let balance = 0;
  let maxBalance = 0;
  let maxLine = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    for (const c of line) {
      if (c === '(') balance++;
      else if (c === ')') balance--;
    }
    if (balance > maxBalance) {
      maxBalance = balance;
      maxLine = i + 1;
    }
    if (balance < 0) return { line: i + 1 };
  }
  if (balance > 0) return { line: maxLine };
  return null;
}

/**
 * Pre-validate common SQL errors and return a clear message (no Oracle call).
 * Returns { error: string, suggestion?: string } or null if no issue found.
 */
export function validateCommonSqlErrors(query) {
  if (!query || typeof query !== 'string') return null;
  const q = query.trim();
  if (!q) return null;

  // 0) AND/OR typos in WHERE (Oracle often calls this ORA-00907)
  const andOrTypo = findAndOrKeywordTypo(q);
  if (andOrTypo) {
    return {
      error: andOrTypo.error,
      suggestion: andOrTypo.suggestion,
      line: andOrTypo.line,
      column: andOrTypo.column,
    };
  }

  // 0+) Reserved aliases (PLAN) / multiple SELECTs
  const structure = findSqlStructureIssue(q);
  if (structure) {
    return {
      error: structure.error,
      suggestion: structure.suggestion,
      line: structure.line,
      column: structure.column,
    };
  }

  // 0a) Missing comma/JOIN between FROM tables (Oracle often calls this ORA-00907)
  const missingJoin = findMissingFromJoin(q);
  if (missingJoin) {
    return {
      error: missingJoin.error,
      suggestion: missingJoin.suggestion,
      line: missingJoin.line,
      column: missingJoin.column,
      nextTable: missingJoin.nextTable,
      previousTable: missingJoin.previousTable,
    };
  }

  // 0b) Missing comma in SELECT (same-line) — BIP often misreports this as catalog/CreateException
  const missingSelectComma = findMissingSelectComma(q);
  if (missingSelectComma) {
    return {
      error: missingSelectComma.error,
      suggestion: missingSelectComma.suggestion,
      line: missingSelectComma.line,
      column: missingSelectComma.column,
    };
  }

  // 1) Unbalanced parentheses ( and )
  const openCount = (q.match(/\(/g) || []).length;
  const closeCount = (q.match(/\)/g) || []).length;
  const balance = openCount - closeCount;
  if (balance !== 0) {
    if (balance > 0) {
      return {
        error: `Unbalanced parentheses: ${openCount} opening ( but ${closeCount} closing ). Add ${balance} closing parenthesis.`,
        suggestion: 'Count every ( and ) — they must match.'
      };
    }
    return {
      error: `Unbalanced parentheses: ${openCount} opening ( but ${closeCount} closing ). Remove ${-balance} extra closing parenthesis.`,
      suggestion: 'Count every ( and ) — they must match.'
    };
  }

  // 2) Unclosed string literal (odd number of single quotes, ignoring escaped '' inside)
  const singleQuotes = (q.match(/'/g) || []).length;
  if (singleQuotes > 0 && singleQuotes % 2 !== 0) {
    return {
      error: 'Unclosed string literal. Check that every opening single quote (\') has a matching closing quote.',
      suggestion: "In Oracle use '' for an apostrophe inside a string, e.g. 'It''s fine'."
    };
  }

  // 3) Comma immediately before FROM
  if (/,\s*FROM\b/i.test(q)) {
    return {
      error: 'Remove the comma before FROM. A trailing comma after the last column is not allowed.',
      suggestion: 'Example: SELECT col1, col2 FROM table_name'
    };
  }

  // 4) Comma immediately before WHERE
  if (/,\s*WHERE\b/i.test(q)) {
    return {
      error: 'Remove the comma before WHERE. A trailing comma after the last column or expression is not allowed.',
      suggestion: 'Example: SELECT col1, col2 FROM t WHERE col1 = 1'
    };
  }

  // 5) Double comma
  if (/,\s*,/.test(q)) {
    return {
      error: 'Remove the extra comma. Two commas in a row are not valid.',
      suggestion: 'Check for missing column/expression between commas or delete the extra comma.'
    };
  }

  // 6) Missing value after = (e.g. WHERE x = or AND status =)
  if (/=\s*(?:AND|OR|\)|$|\s*$)/m.test(q) || /=\s*,\s*\w/.test(q)) {
    return {
      error: 'Missing value after =. Every comparison needs a value on the right side (e.g. WHERE col = \'value\').',
      suggestion: 'Example: WHERE status = \'A\' AND type = 1'
    };
  }

  // 7) IN () empty
  if (/\bIN\s*\(\s*\)/i.test(q)) {
    return {
      error: 'IN () must contain at least one value. Use IN (\'a\', \'b\') or IN (1, 2, 3).',
      suggestion: 'For "no values" use a condition that is always false, e.g. 1=0.'
    };
  }

  // 8) Unclosed block comment /* ... */
  const openBlock = (q.match(/\/\*/g) || []).length;
  const closeBlock = (q.match(/\*\//g) || []).length;
  if (openBlock !== closeBlock) {
    return {
      error: 'Unclosed block comment. Every /* must have a matching */.',
      suggestion: 'Remove the extra /* or add the closing */.'
    };
  }

  // 9) Square brackets (Oracle doesn't use [] for identifiers; might be copy-paste from SQL Server)
  if (/\[|\]/.test(q)) {
    return {
      error: 'Oracle SQL does not use square brackets [ ]. Use double quotes for identifiers with spaces, e.g. "My Column".',
      suggestion: 'Remove [ ] or replace with double quotes if needed.'
    };
  }

  return null;
}
